import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { RichTextEditorHandle } from '../../components/common/RichTextEditor';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
// import CommentForm from '../../components/community/CommentForm';
import RichTextEditor from '../../components/common/RichTextEditor';
import { CommunityLayout } from '../../components/community';
import CoffeeChatPostLayout from '../../components/community/CoffeeChatPostLayout';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import { getPostDetail, createReply, uploadReplyAttachments, updatePost, deletePost, updateReply, deleteReply } from '../../services/postApi';
import { useUserInfo } from '../../hooks/useUserInfo';
import { PostCategory } from '../../types/post';
import { 
  CommunityPost, 
  Comment
} from '../../types/community';
import LoginModal from '../../components/modals/LoginModal';

// 코드 블록 스타일
const CodeBlockStyles = createGlobalStyle`
  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }
`;

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 마이페이지 문의하기 페이지인지 확인
  const isInquiryPage = location.pathname.includes('/mypage/inquiry/post/');
  
  // 커피챗 포스트 페이지인지 확인 (사이드바 숨김 여부 결정)
  const isCoffeeChatPost = location.pathname.includes('/coffee-chat/post/');
  
  // 관리자 경로가 아닌지 확인 (댓글 작성/수정/삭제 가능 여부)
  const isNotAdminPath = !location.pathname.startsWith('/admin');
  
  // 커뮤니티 게시글 상세 경로 여부 (/community/post/:id)
  const isCommunityPostDetail = location.pathname.startsWith('/community/post/');
  
  const [post, setPost] = useState<CommunityPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 이미지 업로드 관련 상태
  // 댓글 입력은 RichTextEditor 사용으로 개별 이미지 선택 미리보기는 사용하지 않음
  // 댓글 입력 에디터 사용으로 이미지 선택 상태 미사용
  // const [isImageUploading, setIsImageUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 수정/삭제 관련 상태
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editPostTitle, setEditPostTitle] = useState('');
  const [editPostContent, setEditPostContent] = useState('');
  const [editCommentContent, setEditCommentContent] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{type: 'post' | 'comment', id?: number} | null>(null);

  // 사용자 정보
  const { userInfo } = useUserInfo();
  
  // 사용자가 관리자인지 확인 (role이 'ADMIN'이거나 email이 특정 관리자 이메일인 경우)
  const isAdmin = userInfo?.role === 'ADMIN' || userInfo?.email === 'admin@example.com';

  // Ref
  // const fileInputRef = useRef<HTMLInputElement>(null);
  // textareaRef는 과거 구현 잔재로 미사용

  // 사용자 설명 생성 함수 (사이드바와 동일한 로직)
  const getUserDescription = (user: any) => {
    // 디버깅을 위한 콘솔 로그
    // /// console.log('사용자 정보:', {user});
    
    // 사용자 아이덴티티를 한글로 변환
    const getIdentityText = (identity: string) => {
      const identityMap = {
        'STUDENT': '의대생',
        'DOCTOR': '의사', 
        'PROFESSOR': '교수',
        'OTHER': '기타'
      };
      return identityMap[identity as keyof typeof identityMap] || identity;
    };

    const identityText = getIdentityText(user.identity);
    let affiliation = '';
    
    // 아이덴티티에 따라 적절한 소속 정보 선택
    if (user.identity === 'STUDENT' && user.university) {
      // 의대생인 경우 대학교 표시
      affiliation = user.university;
    } else if (user.identity === 'DOCTOR' && user.medicine) {
      // 의사인 경우 의료진 소속 (병원 등) 표시
      affiliation = user.medicine;
    } else if (user.identity === 'PROFESSOR') {
      // 교수인 경우 대학교 우선, 없으면 의료진 소속
      affiliation = user.university || user.medicine || '';
    } else {
      // 기타인 경우 또는 소속 정보가 없는 경우
      affiliation = user.university || user.medicine || '';
    }
    
    const result = affiliation ? `${identityText} · ${affiliation}` : identityText;
    // /// console.log('생성된 설명:', result);
    return result;
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`;
  };

  // 게시글 상세 정보 로드
  useEffect(() => {
    const loadPostDetail = async () => {
      if (!postId) {
        navigate('/community');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // API 호출
        const response = await getPostDetail(parseInt(postId));
        
        if (response.data) {
          // API 응답을 컴포넌트에서 사용할 형태로 변환
          const postData: CommunityPost = {
            id: parseInt(postId),
            title: response.data.post_title || "",
            content: response.data.post_content || "",
            mentor_name: response.data.mentor_name || response.data.lecturer_name || "",
            pinned: response.data?.pinned || false,
            author: {
              id: 1,
              name: (response.data.writer_name || "작성자"),
              profileImage: response.data.writer_profile_image_url || "/images/default-avatar.png",
              description: "의대생"
            },
            createdAt: response.data.created_at ? formatDate(response.data.created_at) : "",
            replyCount: response.data.reply_cnt || 0,
            viewCount: response.data.view_cnt || 0,
            category: response.data.post_category || "",
            lecture: response.data.lecture_title ? {
              id: response.data.lecture_id,
              title: response.data.lecture_title,
              chapter: response.data.lecture_chapter
            } : undefined,
            canUpdate: response.data.can_update || false
          };
          // /// console.log('comments@#@#', response.data.replies)
          // 댓글 데이터 변환
          const commentsData: Comment[] = response.data.replies?.map((reply: any) => ({
            id: reply.reply_id,
            postId: parseInt(postId),
            author: {
              id: reply.reply_id,
              name: reply.writer_name,
              profileImage: reply.writer_profile_image_url || "/images/default-avatar.png"
            },
            content: reply.content,
            createdAt: reply.reply_created_at,
            files: reply.images || []
          })) || [];
          /// console.log('postData', postData);
          setPost(postData);
          setComments(commentsData);
        }
        
      } catch (err: any) {
        /// console.error('게시글 로드 실패:', err);
        if (err.response?.status === 401) {
          setShowLoginModal(true);
          return;
        }
        setError('게시글을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadPostDetail();
  }, [postId, navigate]);

  const handleCommentSubmit = async () => {
    if (!newComment.trim() || !post) return;

    try {
      setIsSubmitting(true);
      
      // 댓글은 에디터에서 이미지 삽입을 처리하므로 별도 업로드/주입 없음
      const htmlContent = renderContent(newComment);

      // 3. 댓글 생성 API 호출
      const replyResponse = await createReply({
        postId: post.id,
        htmlContent: htmlContent
      });

      if (replyResponse.code === 200 || replyResponse.code === 201) {
        // 4. 성공 시 댓글 목록에 추가
        const newCommentData: Comment = {
          id: replyResponse.data.reply_id,
          postId: post.id,
          author: {
            id: userInfo?.id || 999,
            name: replyResponse.data.writer_name || userInfo?.name || "게스트",
            profileImage: userInfo?.image_url || "/images/default-avatar.png",
            description: userInfo ? getUserDescription(userInfo) : "사용자"
          },
          content: replyResponse.data.content,
          createdAt: replyResponse.data.reply_created_at,
          files: replyResponse?.data?.images?.map((url: string, index: number) => ({
            id: index,
            name: `이미지 ${index + 1}`,
            url: url
          })) || []
        };
        
        setComments(prev => [...prev, newCommentData]);
        setNewComment('');
        
        /// console.log('댓글 작성 성공:', replyResponse.data);
        
        // 게시글 상세 정보 다시 로드해서 최신 댓글 목록 가져오기
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        
        alert('댓글이 등록되었습니다.');
      } else {
        throw new Error(replyResponse.message || '댓글 등록에 실패했습니다.');
      }
      
    } catch (error: any) {
      /// console.error('댓글 작성 실패:', error);
      
      if (error.message === 'Unauthorized') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        alert(`댓글 작성에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  // 이미지 업로드 -> 서버 전송 후 본문에 img 삽입
  // 사용 안함 (직접 input 클릭)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    try {
      const uploadRes = await uploadReplyAttachments(files);
      const urls: string[] = uploadRes.data || [];
      let htmlContent = renderContent(newComment);
      urls.forEach((u) => {
        htmlContent += `<img src="${u}" alt="첨부 이미지" style="max-width: 50%; height: auto; margin: 10px 0; display: block;" />`;
      });
      setNewComment(htmlContent);
    } catch (err) {
      /// console.error('댓글 이미지 업로드 실패', err);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      e.target.value = '';
    }
  };

  // 코드 블록 삽입 (마크다운 -> 렌더링 시 변환)
  const editorRef = useRef<RichTextEditorHandle>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const insertCodeSnippet = () => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const content = newComment;

    // 토글: 커서가 코드블록 내부이면 블록 전체 언랩
    const openIdx = content.lastIndexOf('```', start);
    const closeIdx = content.indexOf('```', end);
    const insideBlock = openIdx !== -1 && closeIdx !== -1 && openIdx < start && closeIdx >= end;
    if (insideBlock) {
      const unwrapped = content.slice(0, openIdx) + content.slice(openIdx + 3, closeIdx) + content.slice(closeIdx + 3);
      setNewComment(unwrapped);
      setTimeout(() => {
        textarea.focus();
        const caret = Math.max(0, openIdx);
        textarea.setSelectionRange(caret, caret);
      }, 0);
      return;
    }

    // 적용: 선택이 있으면 그 범위를 코드블록으로 감싸기. 없으면 플레이스홀더 삽입
    const before = content.substring(0, start);
    const selected = content.substring(start, end);
    const after = content.substring(end);
    const snippet = `\n\n\`\`\`\n${selected || '// 여기에 코드를 입력하세요'}\n\`\`\`\n\n`;
    const next = before + snippet + after;
    setNewComment(next);
    setTimeout(() => {
      textarea.focus();
      const caret = before.length + 4; // ```\n 바로 뒤
      const selLen = selected ? selected.length : 24;
      textarea.setSelectionRange(caret, caret + selLen);
    }, 0);
  };

  // 파일 변경 핸들러는 사용하지 않음 (에디터 내부에서 처리)

  // 코드 블록 삽입은 에디터 내부에서 처리

  // 마크다운 코드 블록을 HTML로 변환하는 함수
  const renderContent = (content: string) => {
    // ```로 감싸진 코드 블록을 <pre><code>로 변환
    const codeBlockRegex = /```[\w]*\n?([\s\S]*?)```/g;
    const htmlContent = content.replace(codeBlockRegex, (match, code) => {
      // 줄 번호를 위해 각 줄을 span.tbc-line으로 래핑
      const lines = code
        .replace(/\r?\n$/, '')
        .split(/\r?\n/)
        .map((l: string) => `<span class="tbc-line">${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`) 
        .join('\n');
      // 간단 토큰 하이라이트 (태그/속성/문자열/주석)
      let highlighted = lines
        .replace(/(&lt;\/?)([a-zA-Z0-9_-]+)(?=[\s&gt;])/g, '$1<span class="tbc-token-tag">$2</span>')
        .replace(/([a-zA-Z0-9_-]+)=(&quot;.*?&quot;|'.*?')/g, '<span class="tbc-token-attr">$1</span>=<span class="tbc-token-string">$2</span>')
        .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="tbc-token-comment">$1</span>');
      return `<pre><code>${highlighted}</code></pre>`;
    });
    
    // 인라인 코드 `code`를 <code>로 변환
    const inlineCodeRegex = /`([^`]+)`/g;
    const finalContent = htmlContent.replace(inlineCodeRegex, '<code>$1</code>');
    
    // 줄바꿈을 <br>로 변환
    return finalContent.replace(/\n/g, '<br>');
  };

  // 수정/삭제 관련 함수들
  const handleEditPost = () => {
    if (!post) return;
    setEditPostTitle(post.title);
    // 에디터에서는 HTML 그대로 편집
    setEditPostContent(post.content);
    setIsEditingPost(true);
  };

  const handleCancelEditPost = () => {
    setIsEditingPost(false);
    setEditPostTitle('');
    setEditPostContent('');
  };

  const handleSavePost = async () => {
    if (!post || !editPostTitle.trim() || !editPostContent.trim()) {
      alert('제목과 내용을 모두 입력해주세요.');
      return;
    }

    try {
      // 카테고리 타입 매핑
      const categoryMap: { [key: string]: PostCategory } = {
        '강의 질문': '강의 질문',
        '자유': '자유',
        '정보 공유': '정보 공유',
        '공지사항': '공지사항',
        '스터디': '스터디',
        '커피챗': '커피챗',
        '문의하기': '문의하기'
      };
      // /// console.log('수정@#@#', post);  
      const postData = {
        title: editPostTitle,
        htmlContent: renderContent(editPostContent),
        category: categoryMap[post.category] || '자유' as PostCategory,
        lectureId: post.lecture?.id,
        pinned: post?.pinned || false,
        exposed: true
      };

      const response = await updatePost(post.id, postData);
      
      if (response.code === 200 || response.code === 201) {
        // 성공 시 게시글 정보 업데이트
        setPost(prev => prev ? {
          ...prev,
          title: editPostTitle,
          content: editPostContent
        } : null);
        
        setIsEditingPost(false);
        alert('게시글이 수정되었습니다.');
      } else {
        throw new Error(response.message || '게시글 수정에 실패했습니다.');
      }
    } catch (error: any) {
      /// console.error('게시글 수정 실패:', error);
      
      if (error.message === 'Unauthorized') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        alert(`게시글 수정에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      }
    }
  };

  const handleDeletePost = () => {
    setDeleteTarget({ type: 'post' });
    setShowDeleteModal(true);
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    // 에디터에서는 HTML 그대로 편집
    setEditCommentContent(comment.content);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentContent('');
  };

  const handleSaveComment = async (commentId: number) => {
    if (!editCommentContent.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }
    if(!post) return;

    try {
      const response = await updateReply(commentId, editCommentContent, post.id);
      
      if (response.code === 200 || response.code === 201) {
        // 성공 시 댓글 목록 업데이트
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, content: editCommentContent }
            : comment
        ));
        
        setEditingCommentId(null);
        setEditCommentContent('');
        alert('댓글이 수정되었습니다.');
      } else {
        throw new Error(response.message || '댓글 수정에 실패했습니다.');
      }
    } catch (error: any) {
      /// console.error('댓글 수정 실패:', error);
      
      if (error.message === 'Unauthorized') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        alert(`댓글 수정에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      }
    }
  };

  const handleDeleteComment = (commentId: number) => {
    setDeleteTarget({ type: 'comment', id: commentId });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (deleteTarget.type === 'post') {
        if (!post) return;
        
        const response = await deletePost(post.id);
        
        if (response.code === 200 || response.code === 201) {
          alert('게시글이 삭제되었습니다.');
          navigate('/community');
        } else {
          throw new Error(response.message || '게시글 삭제에 실패했습니다.');
        }
      } else if (deleteTarget.type === 'comment' && deleteTarget.id) {
        const response = await deleteReply(deleteTarget.id);
        
        if (response.code === 200 || response.code === 201) {
          // 성공 시 댓글 목록에서 제거
          setComments(prev => prev.filter(comment => comment.id !== deleteTarget.id));
          alert('댓글이 삭제되었습니다.');
        } else {
          throw new Error(response.message || '댓글 삭제에 실패했습니다.');
        }
      }
    } catch (error: any) {
      /// console.error('삭제 실패:', error);
      
      if (error.message === 'Unauthorized') {
        alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        alert(`삭제에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
      }
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // 작성자 확인 함수
  const isPostAuthor = (postAuthorName: string) => {
    // API에서 can_update 값을 받아서 사용
    return post?.canUpdate || false;
  };

  const isCommentAuthor = (commentAuthorName: string) => {
    return userInfo?.name === commentAuthorName;
  };

  // 로그인 성공 후 핸들러
  const handleLoginSuccess = () => {
    // /// console.log('로그인 성공! 모달을 닫습니다.');
    setShowLoginModal(false);
  };

  // 로그인 모달 닫기 핸들러
  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  if (loading) {
    const Layout = isInquiryPage ? MyPageLayout : (isCoffeeChatPost ? CoffeeChatPostLayout : CommunityLayout);
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ fontSize: '18px', color: 'var(--Label-Alternative)' }}>게시글을 불러오는 중...</div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    const Layout = isInquiryPage ? MyPageLayout : (isCoffeeChatPost ? CoffeeChatPostLayout : CommunityLayout);
    return (
      <Layout>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' }}>
          <div style={{ fontSize: '18px', color: 'var(--Label-Normal)' }}>{error || '게시글을 찾을 수 없습니다.'}</div>
          <button
            onClick={() => navigate(isInquiryPage ? '/mypage/inquiry' : '/community')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#448181',
              color: 'white',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            {isInquiryPage ? '문의하기로 돌아가기' : '커뮤니티로 돌아가기'}
          </button>
        </div>
      </Layout>
    );
  }

  const Layout = isInquiryPage ? MyPageLayout : (isCoffeeChatPost ? CoffeeChatPostLayout : CommunityLayout);

  return (
    <Layout>
      <CodeBlockStyles />
      <MainContainer isInquiryPage={isInquiryPage} isCoffeeChatPost={isCoffeeChatPost}>
          {/* 브레드크럼 */}
          <BreadcrumbContainer>
            <BreadcrumbList>
              {isCoffeeChatPost ? (
                <>
                  <BreadcrumbItem>홈</BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                      <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>커피챗</BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                      <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>{post.mentor_name || post.author.name}</BreadcrumbItem>
                </>
              ) : (
                <>
                  <BreadcrumbItem>홈</BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                      <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>커뮤니티</BreadcrumbItem>
                  <BreadcrumbSeparator>
                    <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                      <path d="M1.73 3.07L6.26 7.6L1.73 12.13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>{post.category}</BreadcrumbItem>
                </>
              )}
            </BreadcrumbList>
            {/* 수정/삭제 버튼 (작성자에게만 표시) */}
            {!isEditingPost && isPostAuthor(post.author.name) && (
              <ActionButtons>
                <ActionButton onClick={handleEditPost}>
                  수정
                </ActionButton>
                <ActionButton onClick={handleDeletePost}>
                  삭제
                </ActionButton>
              </ActionButtons>
            )}
          </BreadcrumbContainer>

          {/* 게시글 제목 */}
          {isEditingPost ? (
            <EditPostContainer>
              <input
                value={editPostTitle}
                onChange={(e) => setEditPostTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--Line-Normal-Neutral)',
                  borderRadius: '8px',
                  fontSize: '20px',
                  fontFamily: 'Pretendard JP',
                  fontWeight: '600',
                  lineHeight: '28px',
                  outline: 'none'
                }}
              />
              <RichTextEditor
                value={editPostContent}
                onChange={setEditPostContent}
                placeholder="내용을 입력하세요"
                minHeight="200px"
                attachmentsEditable={true}
              />
              <EditButtonGroup>
                <ActionButton onClick={handleCancelEditPost}>
                  취소
                </ActionButton>
                <ActionButton onClick={handleSavePost}>
                  저장
                </ActionButton>
              </EditButtonGroup>
            </EditPostContainer>
          ) : (
            <PostTitle>{post.title}</PostTitle>
          )}

          {/* 작성자 정보 - 수정 중이 아닐 때만 표시 */}
          {!isEditingPost && (
            <AuthorSection>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Avatar>
                  <img 
                    src={post.author.profileImage} 
                    alt="프로필"
                  />
                </Avatar>
                
                <AuthorDetails>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AuthorName>{post.author.name}</AuthorName>
                    <PostDate>{post.createdAt}</PostDate>
                  </div>
                </AuthorDetails>
              </div>
                
              {!post?.pinned && (
                <PostMetaInfoRight>
                  <PostStat>
                    {post.category === '강의 질문' ? '답변' : '댓글'} {post.replyCount}
                  </PostStat>
                  <MetaDivider />
                  <PostStat>조회수 {post.viewCount}</PostStat>
                </PostMetaInfoRight>
              )}
            </AuthorSection>
          )}

          <div style={{
            alignSelf: 'stretch',
            marginTop: '8px',
            height: '1px',
            background: 'var(--Line-Normal-Normal)'
          }}></div>

          {/* 게시글 내용 */}
          {!isEditingPost && (
            <PostContentContainer
              ref={(el) => {
                if (!el) return;
                el.innerHTML = `<div class="tbc-content">${post.content}</div>`;
                // 복구 처리: 첨부 ul 내부의 불필요 br 제거 및 카드 UI 스타일 적용
                const uls = Array.from(el.querySelectorAll('ul')) as HTMLUListElement[];
                uls.forEach((ul) => {
                  const hasMeaningful = ul.querySelector('li, img, a, span');
                  if (!hasMeaningful && (ul.textContent || '').trim() === '') {
                    ul.remove();
                    return;
                  }
                  ul.querySelectorAll('br').forEach((br) => br.parentNode?.removeChild(br));
                });

                // 마지막 ul을 첨부 리스트로 가정하고 카드 UI로 변환
                const allUls = Array.from(el.querySelectorAll('ul')) as HTMLUListElement[];
                const lastUl = allUls[allUls.length - 1];
                if (lastUl) {
                  lastUl.classList.add('tbc-attachments');
                  const lis = Array.from(lastUl.querySelectorAll('li')) as HTMLLIElement[];
                  lis.forEach((li) => {
                    li.classList.add('tbc-attachment');
                    const a = li.querySelector('a') as HTMLAnchorElement | null;
                    if (!a) return;
                    a.classList.add('tbc-attachment__link');
                    // a 내부 스팬 파싱: 첫번째=파일명, 두번째=원본 URL
                    const spans = Array.from(a.querySelectorAll('span')) as HTMLSpanElement[];
                    const fileName = (spans[0]?.textContent || '').trim();
                    const fileUrl = (spans[1]?.textContent || a.getAttribute('href') || '').trim();
                    // 링크 내용을 비우고 카드 UI 구성
                    a.textContent = '';
                    const thumbWrap = document.createElement('span');
                    thumbWrap.className = 'tbc-attachment__thumb-wrap';
                    // 모든 첨부파일에 클립 모양 아이콘 사용
                    thumbWrap.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.3504 3.9001C9.02491 3.9001 7.95039 4.97461 7.95039 6.3001V16.0501C7.95039 18.2868 9.76364 20.1001 12.0004 20.1001C14.2371 20.1001 16.0504 18.2869 16.0504 16.0501V5.93033C16.0504 5.43327 16.4533 5.03033 16.9504 5.03033C17.4474 5.03033 17.8504 5.43327 17.8504 5.93033V16.0501C17.8504 19.281 15.2313 21.9001 12.0004 21.9001C8.76953 21.9001 6.15039 19.281 6.15039 16.0501V6.3001C6.15039 3.9805 8.0308 2.1001 10.3504 2.1001C12.67 2.1001 14.5504 3.9805 14.5504 6.3001V16.0013C14.5504 17.4096 13.4087 18.5513 12.0004 18.5513C10.5921 18.5513 9.45039 17.4096 9.45039 16.0013V7.18614C9.45039 6.68909 9.85333 6.28614 10.3504 6.28614C10.8474 6.28614 11.2504 6.68909 11.2504 7.18614V16.0013C11.2504 16.4155 11.5862 16.7513 12.0004 16.7513C12.4146 16.7513 12.7504 16.4155 12.7504 16.0013V6.3001C12.7504 4.97461 11.6759 3.9001 10.3504 3.9001Z" fill="currentColor"/></svg>';
                    a.appendChild(thumbWrap);
                    const nameSpan = document.createElement('span');
                    nameSpan.className = 'tbc-attachment__name';
                    nameSpan.textContent = fileName || fileUrl;
                    a.appendChild(nameSpan);
                    // href도 원본 URL로 보정 + 다운로드 속성 부여
                    a.setAttribute('href', fileUrl);
                    a.setAttribute('target', '_blank');
                    a.setAttribute('rel', 'noopener');
                    a.setAttribute('download', fileName || '');
                  });
                }
              }}
            />
          )}

          {/* 첨부파일 */}
          {post.files && post.files.length > 0 && (
            <div style={{
              alignSelf: 'stretch',
              flexDirection: 'column',
              gap: '8px',
              display: 'flex'
            }}>
              {post.files.map((file) => (
                <div key={file.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'rgba(112, 115, 124, 0.05)',
                  border: '1px solid rgba(112, 115, 124, 0.15)',
                  borderRadius: '8px'
                }}>
                  <a href={file.url} target="_blank" rel="noreferrer noopener" download={file.name} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textDecoration: 'none',
                    color: '#171719',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(55, 56, 60, 0.61)',
                      flexShrink: 0
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0', padding: '0' }}>
                        <path d="M10.3504 3.9001C9.02491 3.9001 7.95039 4.97461 7.95039 6.3001V16.0501C7.95039 18.2868 9.76364 20.1001 12.0004 20.1001C14.2371 20.1001 16.0504 18.2869 16.0504 16.0501V5.93033C16.0504 5.43327 16.4533 5.03033 16.9504 5.03033C17.4474 5.03033 17.8504 5.43327 17.8504 5.93033V16.0501C17.8504 19.281 15.2313 21.9001 12.0004 21.9001C8.76953 21.9001 6.15039 19.281 6.15039 16.0501V6.3001C6.15039 3.9805 8.0308 2.1001 10.3504 2.1001C12.67 2.1001 14.5504 3.9805 14.5504 6.3001V16.0013C14.5504 17.4096 13.4087 18.5513 12.0004 18.5513C10.5921 18.5513 9.45039 17.4096 9.45039 16.0013V7.18614C9.45639 6.68909 9.85333 6.28614 10.3504 6.28614C10.8474 6.28614 11.2504 6.68909 11.2504 7.18614V16.0013C11.2504 16.4155 11.5862 16.7513 12.0004 16.7513C12.4146 16.7513 12.7504 16.4155 12.7504 16.0013V6.3001C12.7504 4.97461 11.6759 3.9001 10.3504 3.9001Z" fill="currentColor"/>
                      </svg>
                    </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#171719',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {file.name}
                    </span>
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* 댓글 섹션 - 공지사항이 아닌 경우에만 표시 */}
          {post.category !== '공지사항' && (
            <>
              {/* 답변 구분선 */}
              <div style={{
                alignSelf: 'stretch',
                marginTop: '8px',
                marginBottom: '32px',
                height: '1px',
                background: 'var(--Line-Normal-Normal)'
              }}></div>

              {/* 답변/댓글 헤더 */}
              <div style={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '12px',
                display: 'flex'
              }}>
              <div style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0004 2.35046C6.67084 2.35046 2.3504 6.67092 2.3504 12.0005C2.3504 17.33 6.67084 21.6505 12.0004 21.6505C13.5387 21.6505 14.9951 21.2899 16.2875 20.648L18.0999 21.1423C18.5101 21.2542 18.876 21.354 19.1766 21.4071C19.4811 21.4609 19.8672 21.4975 20.2529 21.3503C20.7581 21.1573 21.1572 20.7582 21.3501 20.253C21.4974 19.8673 21.4607 19.4813 21.407 19.1767C21.3539 18.8761 21.2541 18.5103 21.1422 18.1001L20.6479 16.2876C21.2897 14.9952 21.6503 13.5388 21.6503 12.0005C21.6503 6.67092 17.3299 2.35046 12.0004 2.35046Z" fill="#171719"/>
                </svg>
              </div>
              <div style={{
                color: 'black',
                fontSize: '20px',
                fontFamily: 'Pretendard JP',
                fontWeight: '600',
                lineHeight: '28px'
              }}>
                {post.category === '강의 질문' || post.category === '커피챗' ? '답변' : '댓글'} {comments.length}
              </div>
            </div>

              {/* 댓글 목록 */}
              <div style={{
              alignSelf: 'stretch',
              flexDirection: 'column',
              gap: '32px',
              display: 'flex',
              marginTop: '32px',
            }}>
              {comments.map((comment, index) => (
                <div key={comment.id} style={{
                  alignSelf: 'stretch',
                  flexDirection: 'column',
                  gap: '24px',
                  display: 'flex'
                }}>
                  {/* 댓글 작성자 정보 */}
                  <div style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    display: 'flex'
                  }}>
                    <div style={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '12px',
                      display: 'flex'
                    }}>
                      {/* 댓글 작성자 프로필 */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        outline: '1px var(--Line-Normal-Alternative) solid'
                      }}>
                        <img 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          src={comment.author.profileImage} 
                          alt="프로필"
                        />
                      </div>
                      
                      {/* 댓글 작성자 정보 */}
                      <div style={{
                        flex: '1',
                        flexDirection: 'column',
                        gap: '4px',
                        display: 'flex'
                      }}>
                        <div style={{
                          color: 'var(--Label-Normal)',
                          fontSize: '16px',
                          fontFamily: 'Pretendard JP',
                          fontWeight: '600',
                          lineHeight: '24px',
                          letterSpacing: '0.09px'
                        }}>
                          {comment.author.name}
                        </div>
                        <div style={{
                          color: 'var(--Label-Alternative)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard JP',
                          fontWeight: '400',
                          lineHeight: '16px',
                          letterSpacing: '0.30px'
                        }}>
                          {formatDate(comment.createdAt)}
                        </div>
                      </div>
                    </div>

                    {/* 댓글 수정/삭제 버튼 (관리자는 모든 댓글, 일반 사용자는 자신의 댓글만) */}
                    {isNotAdminPath && editingCommentId !== comment.id && (isAdmin || isCommentAuthor(comment.author.name)) && (
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        <ActionButton onClick={() => handleEditComment(comment)}>
                          수정
                        </ActionButton>
                        <ActionButton onClick={() => handleDeleteComment(comment.id)}>
                          삭제
                        </ActionButton>
                      </div>
                    )}
                  </div>

                  {/* 댓글 내용 */}
                  {editingCommentId === comment.id ? (
                    <div style={{
                      alignSelf: 'stretch',
                      border: '1px solid var(--Line-Normal-Neutral)',
                      borderRadius: '12px',
                      overflow: 'hidden'
                    }}>
                      {/* 텍스트 입력 영역 (RichTextEditor) */}
                      <div style={{ padding: '0', minHeight: '100px' }}>
                        <RichTextEditor
                          value={editCommentContent}
                          onChange={setEditCommentContent}
                          placeholder={post.category === '강의 질문' || post.category === '커피챗' ? '답변을 입력하세요' : '댓글을 입력하세요'}
                          minHeight="120px"
                          attachmentsEditable={true}
                          replyIdForAttachmentDeletion={comment.id}
                          hideAttachmentButton={true}
                          hideToolbar={true}
                          hideBorder={true}
                        />
                      </div>
                      
                      {/* 하단 툴바 */}
                      <div style={{
                        padding: '10px 20px',
                        background: 'var(--Fill-Normal)',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        display: 'flex'
                      }}>
                        {/* 툴바 버튼들 */}
                        <div style={{
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          gap: '16px',
                          display: 'flex'
                        }}>
                          {/* 이미지 버튼 */}
                          <ImageButton onClick={() => fileInputRef.current?.click()}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6.34947 8.74903C6.34947 7.42354 7.42398 6.34903 8.74946 6.34903C10.0749 6.34903 11.1495 7.42354 11.1495 8.74903C11.1495 10.0745 10.0749 11.149 8.74946 11.149C7.42398 11.149 6.34947 10.0745 6.34947 8.74903ZM8.74946 8.14903C8.41809 8.14903 8.14946 8.41766 8.14946 8.74903C8.14946 9.0804 8.41809 9.34903 8.74946 9.34903C9.08083 9.34903 9.34946 9.0804 9.34946 8.74903C9.34946 8.41766 9.08083 8.14903 8.74946 8.14903Z"
                                fill="currentColor"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.26189 2.59961C7.45387 2.5996 6.79298 2.59959 6.25591 2.64347C5.7 2.68889 5.19793 2.78578 4.72904 3.02469C3.99521 3.39859 3.39859 3.99522 3.02468 4.72905C2.78577 5.19794 2.68889 5.70002 2.64347 6.25593C2.59959 6.793 2.5996 7.45385 2.59961 8.26189V15.7373C2.5996 16.5454 2.59959 17.2062 2.64347 17.7433C2.68889 18.2992 2.78577 18.8013 3.02468 19.2702C3.39859 20.004 3.99521 20.6006 4.72904 20.9745C5.19793 21.2135 5.7 21.3103 6.25591 21.3558C6.79299 21.3996 7.45384 21.3996 8.26189 21.3996H15.7373C16.5453 21.3996 17.2062 21.3996 17.7432 21.3558C18.2991 21.3103 18.8012 21.2135 19.2701 20.9745C20.0039 20.6006 20.6006 20.004 20.9745 19.2702C21.2134 18.8013 21.3103 18.2992 21.3557 17.7433C21.3996 17.2062 21.3996 16.5454 21.3995 15.7373V8.26191C21.3996 7.45386 21.3996 6.79301 21.3557 6.25593C21.3103 5.70002 21.2134 5.19794 20.9745 4.72905C20.6006 3.99522 20.0039 3.39859 19.2701 3.02469C18.8012 2.78578 18.2991 2.68889 17.7432 2.64347C17.2062 2.59959 16.5454 2.5996 15.7373 2.59961H8.26189ZM5.54622 4.6285C5.71907 4.54043 5.95789 4.47382 6.40249 4.4375C6.8576 4.40031 7.44466 4.39961 8.29959 4.39961H15.6996C16.5545 4.39961 17.1416 4.40031 17.5967 4.4375C18.0413 4.47382 18.2801 4.54043 18.4529 4.6285C18.8481 4.82983 19.1693 5.15109 19.3707 5.54623C19.4587 5.71908 19.5253 5.95791 19.5617 6.40251C19.5989 6.85761 19.5996 7.44468 19.5996 8.29961V10.8275L19.3735 10.6014C18.9989 10.2267 18.6798 9.90768 18.3965 9.66715C18.0988 9.41446 17.7819 9.19599 17.3956 9.07046C16.8131 8.88122 16.1857 8.88122 15.6033 9.07046C15.2169 9.19599 14.9 9.41446 14.6023 9.66715C14.319 9.90767 14 10.2267 13.6254 10.6014L5.13281 19.0939C4.92563 18.9163 4.75382 18.699 4.62849 18.453C4.54042 18.2802 4.47381 18.0413 4.43749 17.5967C4.4003 17.1416 4.3996 16.5545 4.3996 15.6996V8.29961C4.3996 7.44468 4.4003 6.85761 4.43749 6.40251C4.47381 5.95791 4.54042 5.71908 4.62849 5.54623C4.82982 5.15109 5.15108 4.82983 5.54622 4.6285ZM7.17809 19.5943C7.49378 19.5994 7.86152 19.5996 8.29959 19.5996H15.6996C16.5545 19.5996 17.1416 19.5989 17.5967 19.5617C18.0413 19.5254 18.2801 19.4588 18.4529 19.3707C18.8481 19.1694 19.1693 18.8481 19.3707 18.453C19.4587 18.2802 19.5253 18.0413 19.5617 17.5967C19.5989 17.1416 19.5996 16.5545 19.5996 15.6996V13.373L18.1257 11.8992C17.7192 11.4927 17.4521 11.2266 17.2316 11.0394C17.019 10.8589 16.9097 10.8052 16.8393 10.7824C16.6184 10.7106 16.3804 10.7106 16.1595 10.7824C16.0892 10.8052 15.9798 10.8589 15.7672 11.0394C15.5467 11.2266 15.2796 11.4927 14.8731 11.8992L7.17809 19.5943Z"
                                fill="currentColor"
                              />
                            </svg>
                          </ImageButton>
                          
                          {/* 코드 버튼 */}
                          <CodeButton onClick={insertCodeSnippet}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M14.2187 3.12669C14.7009 3.24725 14.9941 3.73589 14.8736 4.2181L10.8736 20.2181C10.753 20.7003 10.2644 20.9935 9.78217 20.873C9.29996 20.7524 9.00678 20.2638 9.12733 19.7815L13.1273 3.78154C13.2479 3.29932 13.7365 3.00614 14.2187 3.12669Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M16.364 6.36343C16.0126 6.7149 16.0126 7.28475 16.364 7.63622L20.7276 11.9998L16.364 16.3634C16.0126 16.7149 16.0126 17.2847 16.364 17.6362C16.7155 17.9877 17.2854 17.9877 17.6368 17.6362L22.6368 12.6362C22.9883 12.2847 22.9883 11.7149 22.6368 11.3634L17.6368 6.36343C17.2854 6.01195 16.7155 6.01195 16.364 6.36343Z"
                                  fill="currentColor"
                                />
                                <path
                                  d="M7.63696 7.63622C7.98843 7.28475 7.98843 6.7149 7.63696 6.36343C7.28549 6.01195 6.71564 6.01195 6.36417 6.36343L1.36419 11.3634C1.01272 11.7149 1.01272 12.2847 1.36419 12.6362L6.36417 17.6362C6.71564 17.9877 7.28549 17.9877 7.63696 17.6362C7.98843 17.2847 7.98843 16.7149 7.63696 16.3634L3.27337 11.9998L7.63696 7.63622Z"
                                  fill="currentColor"
                                />
                              </svg>
                            </CodeButton>
                          </div>
                          
                          {/* 버튼 그룹 */}
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center'
                          }}>
                            <button
                              onClick={handleCancelEditComment}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--Label-Alternative)',
                                fontSize: '16px',
                                fontFamily: 'Pretendard JP',
                                fontWeight: '600',
                                lineHeight: '24px',
                                letterSpacing: '0.09px'
                              }}
                            >
                              취소
                            </button>
                            <button
                              onClick={() => handleSaveComment(comment.id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--Label-Alternative)',
                                fontSize: '16px',
                                fontFamily: 'Pretendard JP',
                                fontWeight: '600',
                                lineHeight: '24px',
                                letterSpacing: '0.09px'
                              }}
                            >
                              저장하기
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <CommentContentContainer
                        dangerouslySetInnerHTML={{ __html: comment.content }}
                      />
                    )}

                    {/* 댓글에 첨부된 이미지들 표시 */}
                    {comment.files && comment.files.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        marginTop: '12px'
                      }}>
                        {comment.files.map((file, imgIndex) => (
                          <img 
                            key={imgIndex} 
                            src={file.url} 
                            alt={file.name}
                            style={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              borderRadius: '8px',
                              objectFit: 'cover',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* 구분선 */}
                    {index < comments.length - 1 && (
                      <div style={{
                        alignSelf: 'stretch',
                        height: '1px',
                        background: 'var(--Line-Normal-Normal)'
                      }}></div>
                    )}
                  </div>
                ))}
              </div>

              {/* 댓글 입력 구분선 - /admin 경로가 아닌 경우에만 표시 */}
              {isNotAdminPath && (
                <div style={{
                  alignSelf: 'stretch',
                  marginTop: '24px',
                  marginBottom: '32px',
                  height: '1px',
                  background: 'var(--Line-Normal-Normal)'
                }}></div>
              )}

              {/* 댓글 작성 폼 - /admin 경로가 아닌 경우에만 표시 */}
              {isNotAdminPath && (
          <div style={{
            alignSelf: 'stretch',
            flexDirection: 'column',
            gap: '24px',
            display: 'flex'
          }}>
            {/* 댓글 작성자 정보 */}
            <div style={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              display: 'flex'
            }}>
              {/* 현재 사용자 프로필 */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                overflow: 'hidden',
                outline: '1px var(--Line-Normal-Alternative) solid'
              }}>
                <img 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  src={userInfo?.image_url || "/images/default-avatar.png"} 
                  alt="프로필"
                />
              </div>
              
              {/* 현재 사용자 정보 */}
              <div style={{
                flex: '1',
                flexDirection: 'column',
                gap: '4px',
                display: 'flex'
              }}>
                <div style={{
                  color: 'var(--Label-Normal)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard JP',
                  fontWeight: '600',
                  lineHeight: '24px',
                  letterSpacing: '0.09px'
                }}>
                  {userInfo?.name || "게스트"}
                </div>
                <div style={{
                  color: 'var(--Label-Alternative)',
                  fontSize: '12px',
                  fontFamily: 'Pretendard JP',
                  fontWeight: '400',
                  lineHeight: '16px',
                  letterSpacing: '0.30px'
                }}>
                  {userInfo ? getUserDescription(userInfo) : "사용자"}
                </div>
              </div>
            </div>

            {/* 댓글 입력 영역 */}
            <div style={{
              alignSelf: 'stretch',
              border: '1px solid var(--Line-Normal-Neutral)',
              borderRadius: '12px',
              overflow: 'hidden'
            }}>
                {/* 텍스트 입력 영역 (RichTextEditor) */}
               <div style={{ padding: '0', minHeight: '100px' }}>
                 <RichTextEditor
                   ref={editorRef as React.RefObject<RichTextEditorHandle>}
                   value={newComment}
                   onChange={setNewComment}
                   placeholder={post.category === '강의 질문' || post.category === '커피챗' ? '답변을 남겨보세요.' : '댓글을 남겨보세요.'}
                   minHeight="120px"
                   attachmentsEditable={true}
                   hideToolbar={true}
                   hideBorder={true}
                 />
               </div>
              
              {/* 하단 툴바 */}
              <div style={{
                padding: '10px 20px',
                background: 'var(--Fill-Normal)',
                justifyContent: 'space-between',
                alignItems: 'center',
                display: 'flex'
              }}>
                {/* 툴바 버튼들 */}
                <div style={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '16px',
                  display: 'flex'
                }}>
                  {/* 이미지 버튼: 에디터 내부 파일 입력 호출 */}
                  <ImageButton onClick={() => fileInputRef.current?.click()} disabled={isSubmitting}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.34947 8.74903C6.34947 7.42354 7.42398 6.34903 8.74946 6.34903C10.0749 6.34903 11.1495 7.42354 11.1495 8.74903C11.1495 10.0745 10.0749 11.149 8.74946 11.149C7.42398 11.149 6.34947 10.0745 6.34947 8.74903ZM8.74946 8.14903C8.41809 8.14903 8.14946 8.41766 8.14946 8.74903C8.14946 9.0804 8.41809 9.34903 8.74946 9.34903C9.08083 9.34903 9.34946 9.0804 9.34946 8.74903C9.34946 8.41766 9.08083 8.14903 8.74946 8.14903Z"
                        fill="currentColor"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.26189 2.59961C7.45387 2.5996 6.79298 2.59959 6.25591 2.64347C5.7 2.68889 5.19793 2.78578 4.72904 3.02469C3.99521 3.39859 3.39859 3.99522 3.02468 4.72905C2.78577 5.19794 2.68889 5.70002 2.64347 6.25593C2.59959 6.793 2.5996 7.45385 2.59961 8.26189V15.7373C2.5996 16.5454 2.59959 17.2062 2.64347 17.7433C2.68889 18.2992 2.78577 18.8013 3.02468 19.2702C3.39859 20.004 3.99521 20.6006 4.72904 20.9745C5.19793 21.2135 5.7 21.3103 6.25591 21.3558C6.79299 21.3996 7.45384 21.3996 8.26189 21.3996H15.7373C16.5453 21.3996 17.2062 21.3996 17.7432 21.3558C18.2991 21.3103 18.8012 21.2135 19.2701 20.9745C20.0039 20.6006 20.6006 20.004 20.9745 19.2702C21.2134 18.8013 21.3103 18.2992 21.3557 17.7433C21.3996 17.2062 21.3996 16.5454 21.3995 15.7373V8.26191C21.3996 7.45386 21.3996 6.79301 21.3557 6.25593C21.3103 5.70002 21.2134 5.19794 20.9745 4.72905C20.6006 3.99522 20.0039 3.39859 19.2701 3.02469C18.8012 2.78578 18.2991 2.68889 17.7432 2.64347C17.2062 2.59959 16.5454 2.5996 15.7373 2.59961H8.26189ZM5.54622 4.6285C5.71907 4.54043 5.95789 4.47382 6.40249 4.4375C6.8576 4.40031 7.44466 4.39961 8.29959 4.39961H15.6996C16.5545 4.39961 17.1416 4.40031 17.5967 4.4375C18.0413 4.47382 18.2801 4.54043 18.4529 4.6285C18.8481 4.82983 19.1693 5.15109 19.3707 5.54623C19.4587 5.71908 19.5253 5.95791 19.5617 6.40251C19.5989 6.85761 19.5996 7.44468 19.5996 8.29961V10.8275L19.3735 10.6014C18.9989 10.2267 18.6798 9.90768 18.3965 9.66715C18.0988 9.41446 17.7819 9.19599 17.3956 9.07046C16.8131 8.88122 16.1857 8.88122 15.6033 9.07046C15.2169 9.19599 14.9 9.41446 14.6023 9.66715C14.319 9.90767 14 10.2267 13.6254 10.6014L5.13281 19.0939C4.92563 18.9163 4.75382 18.699 4.62849 18.453C4.54042 18.2802 4.47381 18.0413 4.43749 17.5967C4.4003 17.1416 4.3996 16.5545 4.3996 15.6996V8.29961C4.3996 7.44468 4.4003 6.85761 4.43749 6.40251C4.47381 5.95791 4.54042 5.71908 4.62849 5.54623C4.82982 5.15109 5.15108 4.82983 5.54622 4.6285ZM7.17809 19.5943C7.49378 19.5994 7.86152 19.5996 8.29959 19.5996H15.6996C16.5545 19.5996 17.1416 19.5989 17.5967 19.5617C18.0413 19.5254 18.2801 19.4588 18.4529 19.3707C18.8481 19.1694 19.1693 18.8481 19.3707 18.453C19.4587 18.2802 19.5253 18.0413 19.5617 17.5967C19.5989 17.1416 19.5996 16.5545 19.5996 15.6996V13.373L18.1257 11.8992C17.7192 11.4927 17.4521 11.2266 17.2316 11.0394C17.019 10.8589 16.9097 10.8052 16.8393 10.7824C16.6184 10.7106 16.3804 10.7106 16.1595 10.7824C16.0892 10.8052 15.9798 10.8589 15.7672 11.0394C15.5467 11.2266 15.2796 11.4927 14.8731 11.8992L7.17809 19.5943Z"
                        fill="currentColor"
                      />
                    </svg>
                  </ImageButton>
                  
                  {/* 코드 버튼: 선택 유지 후 코드블록 토글 */}
                  <CodeButton onClick={() => {
                    // 에디터의 선택 상태를 그대로 두고 코드 토글을 호출
                    const inst: any = editorRef?.current;
                    if (inst && typeof inst.toggleCodeBlock === 'function') {
                      inst.toggleCodeBlock();
                    } else {
                      insertCodeSnippet(); // 폴백: 텍스트 기반 마크다운 삽입
                    }
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M14.2187 3.12669C14.7009 3.24725 14.9941 3.73589 14.8736 4.2181L10.8736 20.2181C10.753 20.7003 10.2644 20.9935 9.78217 20.873C9.29996 20.7524 9.00678 20.2638 9.12733 19.7815L13.1273 3.78154C13.2479 3.29932 13.7365 3.00614 14.2187 3.12669Z"
                        fill="currentColor"
                      />
                      <path
                        d="M16.364 6.36343C16.0126 6.7149 16.0126 7.28475 16.364 7.63622L20.7276 11.9998L16.364 16.3634C16.0126 16.7149 16.0126 17.2847 16.364 17.6362C16.7155 17.9877 17.2854 17.9877 17.6368 17.6362L22.6368 12.6362C22.9883 12.2847 22.9883 11.7149 22.6368 11.3634L17.6368 6.36343C17.2854 6.01195 16.7155 6.01195 16.364 6.36343Z"
                        fill="currentColor"
                      />
                      <path
                        d="M7.63696 7.63622C7.98843 7.28475 7.98843 6.7149 7.63696 6.36343C7.28549 6.01195 6.71564 6.01195 6.36417 6.36343L1.36419 11.3634C1.01272 11.7149 1.01272 12.2847 1.36419 12.6362L6.36417 17.6362C6.71564 17.9877 7.28549 17.9877 7.63696 17.6362C7.98843 17.2847 7.98843 16.7149 7.63696 16.3634L3.27337 11.9998L7.63696 7.63622Z"
                        fill="currentColor"
                      />
                    </svg>
                  </CodeButton>
                </div>
                
                {/* 등록하기 버튼 */}
                <button
                  onClick={handleCommentSubmit}
                  disabled={!newComment.trim() || isSubmitting}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: (newComment.trim() && !isSubmitting) ? 'pointer' : 'not-allowed',
                    color: (newComment.trim() && !isSubmitting) ? 'var(--Label-Alternative)' : 'var(--Label-Alternative)',
                    fontSize: '16px',
                    fontFamily: 'Pretendard JP',
                    fontWeight: '600',
                    lineHeight: '24px',
                    letterSpacing: '0.09px'
                  }}
                >
                  {isSubmitting ? '저장 중...' : '저장하기'}
                </button>
              </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
          </div>
          )}
            </>
          )}
      </MainContainer>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && createPortal(
        <DeleteModal>
          <DeleteModalContent>
            {/* 경고 아이콘 */}
            <div style={{
              width: '56px',
              height: '56px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="#448181" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 9V13" stroke="#448181" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17H12.01" stroke="#448181" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <DeleteModalTitle>
              {deleteTarget?.type === 'post' ? '게시글을' : '댓글을'} 삭제하시겠습니까?
            </DeleteModalTitle>
            
            <DeleteModalMessage>
              삭제된 {deleteTarget?.type === 'post' ? '게시글' : '댓글'}은 복구할 수 없습니다.
            </DeleteModalMessage>
            
            <DeleteModalButtons>
              <button
                onClick={cancelDelete}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--Line-Normal-Neutral)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'var(--Label-Alternative)',
                  fontSize: '16px',
                  fontFamily: 'Pretendard JP',
                  fontWeight: '500'
                }}
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1,
                  padding: '14px 24px',
                  backgroundColor: '#448181',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '16px',
                  fontFamily: 'Pretendard JP',
                  fontWeight: '500'
                }}
              >
                삭제
              </button>
            </DeleteModalButtons>
          </DeleteModalContent>
        </DeleteModal>,
        document.body
      )}

      {/* 로그인 모달 */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </Layout>
  );
};

// 게시글과 댓글 내용 스타일링
const PostContentContainer = styled.div`
  align-self: stretch;
  color: var(--Label-Normal);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;
  margin: 32px 0;

  /* global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  img {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace !important;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace !important;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }

  /* 첨부파일 UI 스타일 */
  ul.tbc-attachments {
    list-style: none;
    padding: 0;
    margin: 16px 0 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li.tbc-attachment {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: rgba(112, 115, 124, 0.05);
      border: 1px solid rgba(112, 115, 124, 0.15);
      border-radius: 8px;
      position: relative;

      .tbc-attachment__link {
        display: flex;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: #171719;
        flex: 1;
        min-width: 0;

        .tbc-attachment__thumb-wrap {
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(55, 56, 60, 0.61);
          flex-shrink: 0;

          svg {
            width: 14px;
            height: 14px;
            display: block;
            margin: 0;
            padding: 0;
          }
        }

        .tbc-attachment__name {
          font-size: 14px;
          font-weight: 500;
          color: #171719;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .tbc-attachment__delete {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 1px solid rgba(55, 56, 60, 0.61);
        border-radius: 4px;
        cursor: pointer;
        color: rgba(55, 56, 60, 0.61);
        transition: all 0.2s ease;
        flex-shrink: 0;
        text-decoration: none;
        outline: none;

        &::after {
          content: '×';
          font-size: 16px;
          font-weight: 500;
          line-height: 1;
        }

        &:hover {
          background: rgba(255, 59, 48, 0.1);
          color: #ff3b30;
          border-color: #ff3b30;
        }
      }

      &:hover {
        background: rgba(112, 115, 124, 0.08);
        border-color: rgba(112, 115, 124, 0.22);
      }
    }
  }
`;

// 개선된 UI 컴포넌트들
const BreadcrumbContainer = styled.nav`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const BreadcrumbList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.01em;
`;

const BreadcrumbSeparator = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(55, 56, 60, 0.61);
`;

const PostTitle = styled.h1`
  width: 100%;
  color: #171719;
  font-size: 24px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 32px;
  margin: 0;
  margin-bottom: 24px;
  word-wrap: break-word;
`;

const EditPostContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const AuthorSection = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 24px;
  justify-content: space-between;
  align-items: center;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  justify-content: space-between;
`;

const Avatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.08);
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
`;

const AuthorName = styled.span`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  
  @media (max-width: 768px) {\n    font-size: 14px;
  }
`;

const PostMetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const PostMetaInfoRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const PostDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const MetaDivider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.15);
  margin-top: 8px;
`;

const PostStat = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: 0.01em;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  
  @media (max-width: 640px) {
    align-self: flex-start;
  }
`;


const CommentContentContainer = styled.div`
  align-self: stretch;
  color: var(--Label-Normal);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;
  word-wrap: break-word;

  /* global.css의 font: inherit 덮어쓰기 */
  b, strong {
    font-weight: 700 !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  u {
    text-decoration: underline !important;
  }

  strike, s, del {
    text-decoration: line-through !important;
  }

  i, em {
    font-style: italic !important;
    font-family: 'Pretendard JP', sans-serif !important;
  }

  img {
    max-width: 50%;
    height: auto;
    border-radius: 8px;
    margin: 10px 0;
  }

  pre {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    overflow-x: auto;
    
    code {
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
      font-family: 'Courier New', monospace !important;
      font-size: 14px;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  
  code {
    background: var(--Fill-Weak, rgba(112, 115, 124, 0.04));
    border: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace !important;
    font-size: 14px;
    color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  }

  /* 첨부파일 UI 스타일 */
  ul.tbc-attachments {
    list-style: none;
    padding: 0;
    margin: 16px 0 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li.tbc-attachment {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background: rgba(112, 115, 124, 0.05);
      border: 1px solid rgba(112, 115, 124, 0.15);
      border-radius: 8px;
      position: relative;

      .tbc-attachment__link {
        display: flex;
        align-items: center;
        gap: 8px;
        text-decoration: none;
        color: #171719;
        flex: 1;
        min-width: 0;

        .tbc-attachment__thumb-wrap {
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(55, 56, 60, 0.61);
          flex-shrink: 0;

          svg {
            width: 14px;
            height: 14px;
            display: block;
            margin: 0;
            padding: 0;
          }
        }

        .tbc-attachment__name {
          font-size: 14px;
          font-weight: 500;
          color: #171719;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .tbc-attachment__delete {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 1px solid rgba(55, 56, 60, 0.61);
        border-radius: 4px;
        cursor: pointer;
        color: rgba(55, 56, 60, 0.61);
        transition: all 0.2s ease;
        flex-shrink: 0;
        text-decoration: none;
        outline: none;

        &::after {
          content: '×';
          font-size: 16px;
          font-weight: 500;
          line-height: 1;
        }

        &:hover {
          background: rgba(255, 59, 48, 0.1);
          color: #ff3b30;
          border-color: #ff3b30;
        }
      }

      &:hover {
        background: rgba(112, 115, 124, 0.08);
        border-color: rgba(112, 115, 124, 0.22);
      }
    }
  }
`;

// 스타일드 컴포넌트들
const MainContainer = styled.div<{ isInquiryPage?: boolean; isCoffeeChatPost?: boolean }>`
  width: ${props => props.isInquiryPage ? '100%' : (props.isCoffeeChatPost ? '1200px' : '100%')};
  max-width: ${props => props.isInquiryPage ? '894px' : '1200px'};
  padding-top: ${props => props.isInquiryPage ? '62px' : (props.isCoffeeChatPost ? '144px' : '64px')};
  padding-bottom: 128px;
  padding-left: ${props => props.isInquiryPage ? '0' : (props.isCoffeeChatPost ? '0' : '48px')};
  margin: ${props => props.isInquiryPage ? '0 0 0 auto' : (props.isCoffeeChatPost ? '0 auto' : '0 auto')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: 1240px) {
    width: calc(100% - 40px);
    margin: 0 20px;
  }
`;

const ImageButton = styled.button`
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--Label-Normal, #171719);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CodeButton = styled.button`
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--Label-Normal, #171719);
  }
`;

// 댓글 입력은 RichTextEditor 사용으로 이미지 미리보기 전용 컴포넌트는 사용하지 않음

// 게시글과 댓글의 수정/삭제 버튼 스타일
const ActionButton = styled.button`
  height: 32px;
  padding: 7px 14px;
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  backdrop-filter: blur(32px);
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: var(--Fill-Normal, rgba(112, 115, 124, 0.08));
  
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 13px;
  font-family: 'Pretendard JP';
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
  word-wrap: break-word;
  
  &:hover::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--Label-Normal, #171719);
    opacity: 0.04;
    pointer-events: none;
  }
  
  &:active::after {
    opacity: 0.09;
  }
`;

// 삭제 모달 스타일
const DeleteModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999999;
`;

const DeleteModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px 32px 32px 32px;
  max-width: 400px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const DeleteModalTitle = styled.div`
  color: var(--Label-Normal);
  font-size: 18px;
  font-family: 'Pretendard JP';
  font-weight: 600;
  line-height: 26px;
  text-align: center;
`;

const DeleteModalMessage = styled.div`
  color: var(--Label-Alternative);
  font-size: 14px;
  font-family: 'Pretendard JP';
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin-bottom: 8px;
`;

const DeleteModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: stretch;
`;

export default PostDetail;